/**
 * AudienceStep — Study creation wizard step 1.
 *
 * Grid layout grouped by tier-1 industry, matching the audiences lobby.
 * Private audiences get their own "My Audiences" section.
 */

import { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, Search, X, Store, Loader2, AlertCircle, Clock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { isRelaunchingStudyAudienceStatus, isSelectableStudyAudienceStatus } from '@/lib/audience-selectability'
import { groupMarketplaceByIndustry } from '@/lib/utils/audience-industry-grouping'
import { filterAudiences } from '@/lib/utils/audience-filter-predicate'
import { RelaunchingAudienceDialog } from '@/components/audiences/RelaunchingAudienceDialog'
import { AudienceFilterModal, FilterTriggerButton } from '@/pages/AudiencesPage/components/AudienceFilterPanel'
import type { AudienceFilterState } from '@/pages/AudiencesPage/components/AudienceFilterPanel'
import type { AudienceStepProps } from '@/types/components/audience-step'
import type { Population } from '@/types/routes/tests'
import type { AudienceSummary } from '@/lib/api'
import CinematicAudienceCard from '@/pages/AudiencesPage/components/CinematicAudienceCard'
import IndustrySection from '@/pages/AudiencesPage/components/IndustrySection'
import { AUDIENCE_CARD_GRID_CLASS } from '@/pages/AudiencesPage/hooks/useAudienceGridColumns'

function formatRelativeTime(dateString: string) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 14) return '1 week ago'
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString()
}

// ---------------------------------------------------------------------------
// Audience Card Grid
// ---------------------------------------------------------------------------

function AudienceCardGrid({
  audiences,
  onSelect,
  provider,
}: {
  audiences: Population[]
  onSelect: (id: string) => void
  provider: { name: string; logoUrl?: string | null } | null
}) {
  return (
    <div className={AUDIENCE_CARD_GRID_CLASS}>
      {audiences.map((audience) => (
        <div key={audience.id} className="relative">
          <CinematicAudienceCard
            audience={{
              ...audience,
              rowCount: audience.rowCount ?? audience.personaCount ?? null,
            }}
            provider={provider}
            formatRelativeTime={formatRelativeTime}
            onClick={() => onSelect(audience.id)}
            selected={false}
          />
          {audience.status !== 'relaunching' &&
            audience.creditsPerRespondent != null &&
            audience.creditsPerRespondent > 0 && (
            <div className="absolute top-2 right-2 bg-card/90 backdrop-blur-sm border border-border rounded-md px-1.5 py-0.5 text-[14px] font-medium text-muted-foreground">
              {audience.creditsPerRespondent} credit{audience.creditsPerRespondent !== 1 ? 's' : ''}/resp
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function AudienceStatusBadge({ status }: { status: string }) {
  if (status === 'ready') return null
  const config: Record<string, { icon: typeof Loader2; label: string; cls: string }> = {
    processing: { icon: Loader2, label: 'Processing...', cls: 'text-amber-600 dark:text-amber-400' },
    pending: { icon: Clock, label: 'Pending', cls: 'text-muted-foreground' },
    error: { icon: AlertCircle, label: 'Failed', cls: 'text-destructive' },
  }
  const { icon: Icon, label, cls } = config[status] ?? config.pending
  return (
    <div className={cn('flex items-center gap-1 text-xs', cls)}>
      <Icon className={cn('h-3 w-3', status === 'processing' && 'animate-spin')} />
      <span>{label}</span>
    </div>
  )
}

export const AudienceStep = memo(function AudienceStep({
  populations,
  onAudienceClick,
}: AudienceStepProps) {
  const { t } = useTranslation('app')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedCategoryTokens, setSelectedCategoryTokens] = useState<Array<string>>([])
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<Array<string>>([])
  const [audienceTypeFilter, setAudienceTypeFilter] = useState<'B2B' | 'B2C' | null>(null)

  const filterState: AudienceFilterState = useMemo(() => ({
    categoryTokens: selectedCategoryTokens,
    countryCodes: selectedCountryCodes,
    audienceType: audienceTypeFilter,
  }), [selectedCategoryTokens, selectedCountryCodes, audienceTypeFilter])

  const activeFilterCount = selectedCategoryTokens.length + selectedCountryCodes.length + (audienceTypeFilter ? 1 : 0)

  const handleApplyFilters = useCallback((s: AudienceFilterState) => {
    setSelectedCategoryTokens(s.categoryTokens)
    setSelectedCountryCodes(s.countryCodes)
    setAudienceTypeFilter(s.audienceType)
  }, [])

  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }
  }, [searchOpen])

  const filteredPopulations = useMemo(() => {
    if (!searchQuery.trim()) return populations
    const q = searchQuery.toLowerCase()
    return populations.filter((p) => p.name?.toLowerCase().includes(q))
  }, [populations, searchQuery])

  const readyPopulations = useMemo(
    () => filteredPopulations.filter((p) => isSelectableStudyAudienceStatus(p.status)),
    [filteredPopulations],
  )

  const nonReadyPopulations = useMemo(
    () => filteredPopulations.filter((p) => !isSelectableStudyAudienceStatus(p.status)),
    [filteredPopulations],
  )

  const categoryFilteredAudiences = useMemo(
    () => filterAudiences(readyPopulations, filterState),
    [readyPopulations, filterState],
  )

  const yourAudiences = useMemo(() => {
    const bookmarked = readyPopulations
      .filter((p) => p.isBookmarked)
      .sort((a, b) => {
        const ta = a.bookmarkedAt ? new Date(a.bookmarkedAt).getTime() : 0
        const tb = b.bookmarkedAt ? new Date(b.bookmarkedAt).getTime() : 0
        return tb - ta
      })
    const bookmarkedIds = new Set(bookmarked.map((p) => p.id))
    // Non-bookmarked, non-marketplace: owned + shared
    const nonBookmarkedOwn = readyPopulations.filter(
      (p) => p.sharingMode !== 'marketplace' && !bookmarkedIds.has(p.id),
    )
    return [...bookmarked, ...nonBookmarkedOwn]
  }, [readyPopulations])

  const industryRows = useMemo(
    () => groupMarketplaceByIndustry(readyPopulations),
    [readyPopulations],
  )

  const [pendingRelaunchId, setPendingRelaunchId] = useState<string | null>(null)

  const handleSelect = useCallback((id: string) => {
    const pop = populations.find((p) => p.id === id)
    if (!pop || !isSelectableStudyAudienceStatus(pop.status)) return
    if (isRelaunchingStudyAudienceStatus(pop.status)) {
      setPendingRelaunchId(id)
      return
    }
    onAudienceClick(id)
  }, [populations, onAudienceClick])

  if (populations.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="bg-card rounded-2xl border-2 border-dashed border-border p-12 max-w-md">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4">
              <Users className="h-7 w-7 text-muted-foreground" />
            </div>
            <Heading level={3} className="mb-2">
              {t('creation.noAudiencesAvailable')}
            </Heading>
            <Text size="sm" color="muted" className="max-w-sm">
              {t('creation.noAudiencesHint')}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto pb-28">
      <div className="space-y-8">
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
            {t('creation.chooseAudienceTitle')}
          </h1>
          <div className="flex items-center gap-2">
            <FilterTriggerButton
              activeCount={activeFilterCount}
              onClick={() => setFilterOpen(true)}
            />
            <div
              className={cn(
                'flex h-10 items-center overflow-hidden rounded-lg border-[1.5px] border-foreground dark:border-accent-highlight bg-background/45 transition-all duration-200',
                searchOpen || searchQuery ? 'w-[320px] px-2' : 'w-10 px-0',
              )}
            >
            <button
              type="button"
              aria-label={t('creation.searchAudiencesAria')}
              onClick={() => setSearchOpen(true)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-foreground dark:text-accent-highlight"
            >
              <Search className="h-4 w-4 -translate-x-px -translate-y-px" />
            </button>
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={t('creation.searchAudiencesPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'h-10 flex-1 border-0 bg-transparent px-0 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 font-sans',
                !(searchOpen || searchQuery) && 'pointer-events-none opacity-0',
              )}
            />
            <button
              type="button"
              aria-label={t('creation.clearSearch')}
              onClick={() => {
                setSearchQuery('')
                setSearchOpen(false)
              }}
              className={cn(
                'inline-flex h-10 w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground',
                !(searchOpen || searchQuery) && 'pointer-events-none opacity-0',
              )}
            >
              <X className="h-4 w-4" />
            </button>
            </div>
          </div>
        </div>

        <AudienceFilterModal
          open={filterOpen}
          onOpenChange={setFilterOpen}
          state={filterState}
          onApply={handleApplyFilters}
          audiences={populations as unknown as Array<AudienceSummary>}
        />

        {categoryFilteredAudiences !== null ? (
          /* Filtered view — flat grid */
          <div className="space-y-6">
            <Heading level={2}>
              {t('audiences.matchingFilters', { count: categoryFilteredAudiences.length })}
            </Heading>
            {categoryFilteredAudiences.length > 0 ? (
              <AudienceCardGrid
                audiences={categoryFilteredAudiences}
                onSelect={handleSelect}
                provider={null}
              />
            ) : (
              <div className="flex flex-col items-center py-20 text-center">
                <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <Search className="h-7 w-7 text-muted-foreground" />
                </div>
                <Heading level={3} className="mb-1.5">
                  {t('creation.noFilterMatch')}
                </Heading>
                <Text size="sm" color="muted">
                  {t('creation.noFilterMatchHint')}
                </Text>
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Your audiences section */}
        {yourAudiences.length > 0 && (
          <div className="space-y-6">
            <h2 className="font-display text-3xl text-foreground" style={{ letterSpacing: '-0.02em' }}>
              {t('audiences.title')}
            </h2>
            <AudienceCardGrid
              audiences={yourAudiences}
              onSelect={handleSelect}
              provider={null}
            />
          </div>
        )}

        {/* Marketplace section — grouped by tier-1 industry, matching the lobby */}
        {industryRows.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[hsl(var(--audience-badge-bg))] shrink-0">
                <Store className="h-3 w-3 text-[hsl(var(--audience-badge-fg))]" />
              </div>
              <h2 className="font-display text-3xl text-foreground" style={{ letterSpacing: '-0.02em' }}>
                {t('audiences.marketplace')}
              </h2>
            </div>

            {industryRows.map(({ industry, audiences: industryAudiences }, sectionIndex) => (
              <div
                key={industry.token}
                className={cn(sectionIndex > 0 && 'pt-6 mt-6')}
              >
                <IndustrySection
                  industry={industry}
                  audiences={industryAudiences.map((a) => ({
                    ...a,
                    rowCount: a.rowCount ?? a.personaCount ?? null,
                  }))}
                  formatRelativeTime={formatRelativeTime}
                  onCardClick={handleSelect}
                />
              </div>
            ))}
          </div>
        )}

        {/* Not yet ready */}
        {nonReadyPopulations.length > 0 && (
          <div className="space-y-6">
            <h2 className="font-display text-4xl text-muted-foreground" style={{ letterSpacing: '-0.02em' }}>
              {t('creation.notYetReady')}
            </h2>
            <div className={AUDIENCE_CARD_GRID_CLASS}>
              {nonReadyPopulations.map((audience) => (
                <div key={audience.id} className="opacity-60 cursor-not-allowed relative">
                  <CinematicAudienceCard
                    audience={{
                      ...audience,
                      rowCount: audience.rowCount ?? audience.personaCount ?? null,
                    }}
                    provider={null}
                    formatRelativeTime={formatRelativeTime}
                    onClick={() => {}}
                    selected={false}
                  />
                  {audience.status !== 'error' && (
                    <div className="absolute top-2 right-2">
                      <AudienceStatusBadge status={audience.status ?? 'pending'} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {searchQuery && filteredPopulations.length === 0 && (
          <div className="text-center py-8">
            <Text color="muted">{t('creation.noSearchMatch', { query: searchQuery })}</Text>
          </div>
        )}
        </>
        )}
      </div>

      <RelaunchingAudienceDialog
        open={!!pendingRelaunchId}
        onCancel={() => setPendingRelaunchId(null)}
        onConfirm={() => {
          if (pendingRelaunchId) onAudienceClick(pendingRelaunchId)
          setPendingRelaunchId(null)
        }}
      />
    </div>
  )
})
